import React, { CSSProperties, ReactElement } from 'react';
import ReactDOM from 'react-dom';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import { useId } from '@fluentui/react-hooks';
import ResizeHandle from './ResizeHandle';
import joinClasses from 'classnames';
import { Tooltip } from '@fluentui/react-components';

require('styles/react-data-grid/react-data-grid-header.css');

const simpleCellRenderer = (props: { column: IEditableDataGridColumnDef }): ReactElement => {
  const column = props.column;
  const tooltipId = useId(`tooltip${column.key as string}`);
  const headerText = column.rowType === 'header' ? column.name : '';
  return (
    <div className="widget-HeaderCell__value" style={column.styles?.headerCell} aria-describedby={tooltipId}>
      {headerText}
    </div>
  );
};

type IHeaderCellProps = {
  renderer: any;
  column: IEditableDataGridColumnDef & { left: number };
  onResize: (column: IEditableDataGridColumnDef, width: number) => void;
  onResizeEnd: (column: IEditableDataGridColumnDef, width: number) => void;
  height: number;
  className?: string;
};

class HeaderCell extends React.Component<IHeaderCellProps, { resizing: boolean }> {
  static defaultProps = {
    renderer: simpleCellRenderer,
  };

  state = { resizing: false };

  onDragStart = (e: MouseEvent) => {
    this.setState({ resizing: true });
  };

  onDrag = (e: MouseEvent) => {
    if (this.props.onResize) {
      const width = this.getWidthFromMouseEvent(e);
      if (width > 0) {
        this.props.onResize(this.props.column, width);
      }
    }
  };

  onDragEnd = (e: MouseEvent) => {
    const width = this.getWidthFromMouseEvent(e);
    this.props.onResizeEnd(this.props.column, width);
    this.setState({ resizing: false });
  };

  getWidthFromMouseEvent = (e: MouseEvent | TouchEvent): number => {
    const right =
      (e instanceof MouseEvent && e.pageX) ||
      (e instanceof TouchEvent && e.touches && e.touches[0] && e.touches[0].pageX) ||
      (e instanceof TouchEvent && e.changedTouches && e.changedTouches[e.changedTouches.length - 1].pageX) ||
      0;
    const left = ReactDOM.findDOMNode(this).getBoundingClientRect().left;
    return right - left;
  };

  getCell = (): ReactElement => {
    if (React.isValidElement(this.props.renderer)) {
      // if it is a string, it's an HTML element, and column is not a valid property, so only pass height
      if (typeof this.props.renderer.type === 'string') {
        return React.cloneElement(this.props.renderer, {
          height: this.props.height,
        } as any);
      }
      return React.cloneElement(this.props.renderer, {
        column: this.props.column,
        height: this.props.height,
      } as any);
    }
    const Renderer = this.props.renderer;
    return <Renderer column={this.props.column} />;
  };

  setScrollLeft = (scrollLeft: number) => {
    const node = ReactDOM.findDOMNode(this);
    node.style.webkitTransform = `translate3d(${scrollLeft}px, 0px, 0px)`;
    node.style.transform = `translate3d(${scrollLeft}px, 0px, 0px)`;
  };

  removeScroll = () => {
    const node = ReactDOM.findDOMNode(this);
    if (node) {
      const transform = 'none';
      node.style.webkitTransform = transform;
      node.style.transform = transform;
    }
  };

  render(): ReactElement {
    const headerCellStyles: CSSProperties = {
      width: this.props.column.width,
      left: this.props.column.left,
      display: 'inline-block',
      position: 'absolute',
      height: this.props.height,
      margin: 0,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...this.props.column.styles?.header,
    };

    let resizeHandle;
    if (this.props.column.resizable) {
      resizeHandle = <ResizeHandle onDrag={this.onDrag} onDragStart={this.onDragStart} onDragEnd={this.onDragEnd} />;
    }
    let className = joinClasses({
      'react-grid-HeaderCell': true,
      'react-grid-HeaderCell--resizing': this.state.resizing,
      'react-grid-HeaderCell--locked': this.props.column.locked,
    });
    className = joinClasses(className, this.props.className, this.props.column.cellClass);
    const cell = this.getCell();
    const column = this.props.column;
    const tooltipText = column.tooltip ? `${column.name}: ${column.tooltip}` : column.name;

    const headerCell = (
      <div className={className} style={headerCellStyles}>
        {cell}
        {resizeHandle}
      </div>
    );
    return tooltipText ? (
      <Tooltip content={tooltipText} relationship={'label'} withArrow={true}>
        {headerCell}
      </Tooltip>
    ) : (
      headerCell
    );
  }
}

export default HeaderCell;
